import React, { useCallback, useEffect, useRef, useState } from "react";
import { Field } from "react-final-form";
import {
  Box,
  Button,
  FormHelperText,
  Tab,
  Tabs,
  TextField,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckRounded from "@mui/icons-material/CheckRounded";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import colors from "../../../theme/colors";
import formatDateTime from "../../../_src_shared/utils/functions/formatDateTime";
import RffInputWrapper from "../shared/RffInputWrapper";

/**
 * Script faces offered on the Type tab. `family` must match a face loaded in
 * public/index.html — canvas silently falls back to a default font when the face
 * isn't available, which would produce a signature in the wrong hand.
 *
 * `previewSize` differs per face because these scripts have very different
 * x-heights; at one shared size Homemade Apple dwarfs Great Vibes.
 */
const TYPE_FONTS = [
  { name: "Caveat", family: '"Caveat", cursive', previewSize: 34 },
  { name: "Dancing Script", family: '"Dancing Script", cursive', previewSize: 30 },
  { name: "Great Vibes", family: '"Great Vibes", cursive', previewSize: 32 },
  { name: "Homemade Apple", family: '"Homemade Apple", cursive', previewSize: 24 },
];

const MODES = [
  { value: "draw", label: "Draw" },
  { value: "type", label: "Type" },
  { value: "upload", label: "Upload" },
];

const ACCEPTED_UPLOAD = "image/png,image/jpeg";
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
const CANVAS_HEIGHT = 150;

/**
 * RffSignatureField — signature capture offering three ways to sign: draw with a
 * pointer, type a name in a script face, or upload an image.
 *
 * The form value is a trimmed PNG data URL regardless of which mode produced it,
 * so nothing downstream (payload, PDF rendering) has to know how it was signed.
 *
 * Deliberately does NOT persist the signature anywhere but the form: these forms
 * are public and unauthenticated, and caching a borrower's signature image would
 * leave it on a shared machine for whoever sits down next.
 */
const RffSignatureField = ({
  name,
  label,
  required = false,
  helperText,
  suppressGrid,
  size,
  height = CANVAS_HEIGHT,
  sx,
}) => (
  <RffInputWrapper
    label={label}
    suppressGrid={suppressGrid}
    size={size}
    helperText={helperText}
    required={required}
  >
    <Field
      name={name}
      validate={(v) =>
        required && (v == null || v === "") ? "A signature is required" : undefined
      }
      subscription={{ value: true, error: true, touched: true }}
      render={({ input, meta }) => (
        <Flx column gap={1} sx={sx}>
          <SignatureCreator
            value={input.value}
            onChange={input.onChange}
            height={height}
          />
          {meta.touched && meta.error && (
            <FormHelperText error>{meta.error}</FormHelperText>
          )}
        </Flx>
      )}
    />
  </RffInputWrapper>
);

/**
 * The capture card. Each mode *stages* a signature; Adopt is what commits it to
 * the form. All three modes' state lives here rather than in the tabs, so that
 * switching tabs can clear what was staged — a tab that unmounts takes its
 * canvas with it, and a stale "ready to adopt" flag would adopt an empty pad.
 */
const SignatureCreator = ({ value, onChange, height }) => {
  const [mode, setMode] = useState("draw");
  const padRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [font, setFont] = useState(TYPE_FONTS[0]);
  const [uploadUrl, setUploadUrl] = useState(null);
  const [uploadError, setUploadError] = useState("");

  const trimmedName = typedName.trim();

  // A single character isn't a name.
  const canAdopt =
    mode === "draw"
      ? hasInk
      : mode === "type"
        ? trimmedName.length > 1
        : Boolean(uploadUrl);

  const changeMode = (next) => {
    setHasInk(false);
    setUploadUrl(null);
    setUploadError("");
    setMode(next);
  };

  const adopt = async () => {
    if (mode === "draw") {
      const dataUrl = padRef.current?.toTrimmedDataUrl();
      if (dataUrl) onChange(dataUrl);
      return;
    }
    if (mode === "type") {
      if (trimmedName.length <= 1) return;
      // Wait for the chosen face specifically — canvas gives no fallback signal,
      // so drawing before it loads silently yields the wrong typeface.
      try {
        await document.fonts?.load?.(`80px ${font.family}`);
      } catch (e) {
        // Render anyway; a default face beats a dead button.
      }
      onChange(renderTypedSignature(trimmedName, font.family));
      return;
    }
    if (uploadUrl) onChange(uploadUrl);
  };

  if (value) {
    return <SignaturePreview value={value} onClear={() => onChange(null)} />;
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.slate200}`,
        borderRadius: 2.5,
        overflow: "hidden",
        background: colors.white,
      }}
    >
      <Tabs
        value={mode}
        onChange={(e, next) => changeMode(next)}
        sx={{
          minHeight: 0,
          px: 2.5,
          background: colors.slate50,
          borderBottom: `1px solid ${colors.slate200}`,
          "& .MuiTab-root": {
            minHeight: 0,
            minWidth: 0,
            py: 1.25,
            px: 0,
            mr: 3.5,
            fontSize: 13.5,
            fontWeight: 500,
            textTransform: "none",
            color: colors.slate500,
            "&.Mui-selected": { color: colors.slate900, fontWeight: 600 },
          },
        }}
      >
        {MODES.map((m) => (
          <Tab key={m.value} value={m.value} label={m.label} disableRipple />
        ))}
      </Tabs>

      <Box sx={{ p: 2 }}>
        {mode === "draw" && (
          <DrawPad
            ref={padRef}
            hasInk={hasInk}
            onInk={() => setHasInk(true)}
            height={height}
          />
        )}
        {mode === "type" && (
          <TypePanel
            typedName={typedName}
            setTypedName={setTypedName}
            font={font}
            setFont={setFont}
          />
        )}
        {mode === "upload" && (
          <UploadPanel
            height={height}
            uploadUrl={uploadUrl}
            error={uploadError}
            onPick={(url) => {
              setUploadUrl(url);
              setUploadError("");
            }}
            onError={(msg) => {
              setUploadUrl(null);
              setUploadError(msg);
            }}
          />
        )}
      </Box>

      <Flx between centerY gap={1} sx={{ px: 2, pb: 2 }}>
        {mode === "draw" && hasInk ? (
          <Button
            variant="text"
            size="small"
            onClick={() => {
              padRef.current?.clear();
              setHasInk(false);
            }}
          >
            Clear
          </Button>
        ) : (
          <Box />
        )}
        {/* Outlined, not contained: this commits the signature to the field, it
            does not submit the form. Two solid primary buttons a few rows apart
            read as the same weight of action, and the one that actually files
            the authorization should be the only filled one on the page. */}
        <Button
          variant="outlined"
          size="small"
          disabled={!canAdopt}
          onClick={adopt}
          startIcon={<CheckRounded sx={{ fontSize: 16 }} />}
          sx={{
            px: 2,
            borderWidth: 1.5,
            "&:hover": { borderWidth: 1.5 },
          }}
        >
          Adopt signature
        </Button>
      </Flx>
    </Box>
  );
};

/**
 * The adopted signature, with the date it was adopted.
 *
 * The date is display-only — it records nothing. A legally meaningful signed-at
 * timestamp has to be stamped server-side at submit; the browser clock can't be
 * trusted for it.
 */
const SignaturePreview = ({ value, onClear }) => {
  const theme = useTheme();
  const adoptedOn = formatDateTime(new Date().toISOString(), "date");

  return (
    <Box
      sx={{
        border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
        background: alpha(theme.palette.primary.main, 0.06),
        borderRadius: 2.5,
        p: 2.5,
      }}
    >
      <Flx between gap={2} sx={{ alignItems: "flex-start" }}>
        <Flx column gap={1.25} sx={{ minWidth: 0 }}>
          <Box
            component="img"
            src={value}
            alt="Your signature"
            sx={{
              height: 56,
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
          <Flx centerY gap={0.75}>
            <CheckRounded
              sx={{ fontSize: 15, color: theme.palette.primary.main }}
            />
            <Txt
              component="div"
              sx={{ fontSize: 12.5, color: theme.palette.primary.main }}
            >
              {`Signature adopted · ${adoptedOn}`}
            </Txt>
          </Flx>
        </Flx>
        <Button
          variant="outlined"
          size="small"
          onClick={onClear}
          sx={{ flexShrink: 0, background: colors.white }}
        >
          Change
        </Button>
      </Flx>
    </Box>
  );
};

/**
 * Freehand pad.
 *
 * Backed directly by a canvas and pointer events rather than a signature
 * library: the drawing surface is a handful of lineTo calls, and owning it here
 * is what lets `toTrimmedDataUrl` crop to the ink's bounding box — the exported
 * PNG carries no surrounding whitespace, so it sits tight wherever it's
 * rendered later.
 *
 * The canvas is sized in device pixels and scaled back down in CSS, otherwise
 * strokes are visibly soft on high-DPI screens.
 */
const DrawPad = React.forwardRef(function DrawPad({ hasInk, onInk, height }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  // Ink bounding box in canvas coordinates, grown as the pointer moves.
  const bounds = useRef(null);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const { width } = canvas.getBoundingClientRect();
    if (!width) return;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = colors.slate900;
  }, [height]);

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);

  const pointAt = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const track = (point) => {
    const b = bounds.current;
    if (!b) {
      bounds.current = { minX: point.x, minY: point.y, maxX: point.x, maxY: point.y };
      return;
    }
    b.minX = Math.min(b.minX, point.x);
    b.minY = Math.min(b.minY, point.y);
    b.maxX = Math.max(b.maxX, point.x);
    b.maxY = Math.max(b.maxY, point.y);
  };

  const start = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    canvas.setPointerCapture(e.pointerId);
    const ctx = canvas.getContext("2d");
    const point = pointAt(e);
    drawing.current = true;
    track(point);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    onInk();
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const point = pointAt(e);
    track(point);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const end = () => {
    drawing.current = false;
  };

  React.useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      bounds.current = null;
    },
    toTrimmedDataUrl: () => {
      const canvas = canvasRef.current;
      const b = bounds.current;
      if (!canvas || !b) return null;

      const ratio = window.devicePixelRatio || 1;
      // A little breathing room around the strokes so the pen width isn't clipped.
      const pad = 6;
      const x = Math.max(0, (b.minX - pad) * ratio);
      const y = Math.max(0, (b.minY - pad) * ratio);
      const w = Math.min(canvas.width - x, (b.maxX - b.minX + pad * 2) * ratio);
      const h = Math.min(canvas.height - y, (b.maxY - b.minY + pad * 2) * ratio);
      if (w <= 0 || h <= 0) return canvas.toDataURL("image/png");

      const out = document.createElement("canvas");
      out.width = Math.round(w);
      out.height = Math.round(h);
      out.getContext("2d").drawImage(canvas, x, y, w, h, 0, 0, out.width, out.height);
      return out.toDataURL("image/png");
    },
  }));

  return (
    <Box
      sx={{
        position: "relative",
        border: `1px solid ${colors.slate300}`,
        borderRadius: 2,
        overflow: "hidden",
        background: colors.white,
      }}
    >
      {/* Prompt and baseline sit beneath the canvas so they read as part of the
          pad without intercepting the pointer. */}
      {!hasInk && (
        <Txt
          component="div"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "42%",
            textAlign: "center",
            fontSize: 13,
            color: colors.slate400,
            pointerEvents: "none",
          }}
        >
          Sign here using your mouse or finger
        </Txt>
      )}
      <Box
        sx={{
          position: "absolute",
          left: 24,
          right: 24,
          bottom: 34,
          borderBottom: `1px dashed ${colors.slate300}`,
          pointerEvents: "none",
        }}
      />
      <Box
        component="canvas"
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        sx={{
          display: "block",
          position: "relative",
          zIndex: 1,
          width: "100%",
          height: `${height}px`,
          touchAction: "none",
          cursor: "crosshair",
        }}
      />
    </Box>
  );
});

const TypePanel = ({ typedName, setTypedName, font, setFont }) => {
  const theme = useTheme();
  const trimmed = typedName.trim();

  // Ask for the faces up front so the tiles preview in the real hand rather than
  // swapping in after a choice has already been made.
  useEffect(() => {
    if (!document.fonts?.load) return;
    TYPE_FONTS.forEach((f) => {
      document.fonts.load(`64px ${f.family}`).catch(() => {});
    });
  }, []);

  return (
    <Flx column gap={1.5}>
      <TextField
        size="small"
        fullWidth
        placeholder="Type your full legal name"
        value={typedName}
        onChange={(e) => setTypedName(e.target.value)}
      />
      {/* auto-fit rather than a fixed 2-up: the tiles hold a name at ~30px, and
          a long one needs the full width on a narrow viewport. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 1.25,
        }}
      >
        {TYPE_FONTS.map((f) => {
          const selected = f.name === font.name;
          return (
            <Box
              key={f.name}
              component="button"
              type="button"
              onClick={() => setFont(f)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 68,
                px: 2,
                py: 1.25,
                cursor: "pointer",
                borderRadius: 2,
                overflow: "hidden",
                transition: "all 120ms",
                background: selected
                  ? alpha(theme.palette.primary.main, 0.06)
                  : colors.white,
                border: `1px solid ${
                  selected ? theme.palette.primary.main : colors.slate200
                }`,
                "&:hover": {
                  background: selected
                    ? alpha(theme.palette.primary.main, 0.06)
                    : colors.slate50,
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: f.family,
                  fontSize: f.previewSize,
                  lineHeight: 1.3,
                  color: trimmed ? colors.slate900 : colors.slate400,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {trimmed || "Your Name"}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Flx>
  );
};

const UploadPanel = ({ height, uploadUrl, error, onPick, onError }) => {
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      onError("That image is over 4MB. Please upload a smaller file.");
      return;
    }
    try {
      onPick(await fileToDataUrl(file));
    } catch (err) {
      onError("We couldn't read that file. Please try another image.");
    }
  };

  return (
    <Flx column gap={1}>
      <Box
        component="button"
        type="button"
        onClick={() => inputRef.current?.click()}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          width: "100%",
          minHeight: height,
          p: 2,
          cursor: "pointer",
          borderRadius: 2,
          background: colors.slate50,
          border: `1.5px dashed ${colors.slate300}`,
          "&:hover": { background: colors.slate100 },
        }}
      >
        {uploadUrl ? (
          <Box
            component="img"
            src={uploadUrl}
            alt="Your signature"
            sx={{ maxHeight: 90, maxWidth: "100%", objectFit: "contain" }}
          />
        ) : (
          <>
            <FileUploadOutlined sx={{ color: colors.slate500 }} />
            <Txt component="div" sx={{ fontSize: 13.5, color: colors.slate600 }}>
              Upload an image of your signature
            </Txt>
            <Txt component="div" sx={{ fontSize: 12, color: colors.slate400 }}>
              PNG or JPG on a plain background works best
            </Txt>
          </>
        )}
      </Box>
      <Box
        component="input"
        ref={inputRef}
        type="file"
        accept={ACCEPTED_UPLOAD}
        onChange={handleFile}
        sx={{ display: "none" }}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Flx>
  );
};

/**
 * Draw the typed name to a tightly-sized PNG, so a typed signature and a drawn
 * one are the same kind of value to everything downstream.
 */
function renderTypedSignature(text, fontFamily) {
  const fontSize = 80;
  const pad = 20;

  const measure = document.createElement("canvas").getContext("2d");
  measure.font = `${fontSize}px ${fontFamily}`;
  const width = Math.ceil(measure.measureText(text).width) + pad * 2;
  const height = fontSize + pad * 2;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.fillStyle = "#111111";
  ctx.textBaseline = "middle";
  ctx.fillText(text, pad, height / 2);
  return canvas.toDataURL("image/png");
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default RffSignatureField;
